import { ModalWizard } from '@monorepo/shared/componentsV2/modalWizard/modalWizard';
import { getCache as getDatasetCache } from '@monorepo/shared/hooks/datasets/useDataset';
import { findStepInputDataset } from '@monorepo/workflowDataSetup/src/components/EditWorkflowStepsPage/stepUtils';
import JoinIcon from '@svg/join.svg';
import {
  DatasetResponse,
  DatetimeOriginTransformation,
  JoinStepConditionResponse,
  JoinWorkflowStepResponse,
  SaveWorkflowJoinStepRequest,
  WorkflowStepOperationType,
} from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import { DeepPartial } from 'utility-types';
import { CommonEditStepModalProps } from '../BaseEditStepModal';
import {
  isDateIntervalCondition as getIsDateIntervalCondition,
  getTimeSeriesDateRangeByTransformation,
  getTimeSeriesDateRangeProperties,
} from './helpers';
import { JoinConditionsModalPage } from './JoinConditionsModalPage';
import { JoinToModalPage } from './JoinToModalPage';
import { JoinTypeModalPage } from './JoinTypeModalPage';

type FormValues = Pick<
  SaveWorkflowJoinStepRequest['operationConfig'],
  'conditions' | 'joinToDatasetId' | 'joinToPrefix' | 'joinType'
> & {
  dateInterval?: DatetimeOriginTransformation;
};

function parseStepOperationConfigToFormValues(
  operationConfig: JoinWorkflowStepResponse['operationConfig'],
  inputDataset: DatasetResponse,
): DeepPartial<FormValues> {
  const inputTimeSeriesDateRanges =
    getTimeSeriesDateRangeProperties(inputDataset);
  const joinToTimeSeriesDateRanges = getTimeSeriesDateRangeProperties(
    operationConfig.joinToDataset,
  );
  let dateIntervalCondition: JoinStepConditionResponse | undefined;
  const filteredConditions = operationConfig.conditions.filter((condition) => {
    const isDateIntervalCondition = getIsDateIntervalCondition(
      condition,
      inputTimeSeriesDateRanges,
      joinToTimeSeriesDateRanges,
    );
    if (isDateIntervalCondition) {
      dateIntervalCondition = condition;
    }
    return !condition.readOnly && !isDateIntervalCondition;
  });
  let dateInterval: DatetimeOriginTransformation | undefined;
  if (dateIntervalCondition) {
    // It doesn't matter which datetime transformation we return (from left or right property),
    // since they should be the same
    const { leftPropertyId } = dateIntervalCondition;
    const leftProperty = inputDataset.schemaProperties.find(
      (p) => p.id === leftPropertyId,
    );
    dateInterval = leftProperty?.dateTransformation || undefined;
  }
  return {
    conditions: filteredConditions,
    dateInterval,
    joinToDatasetId: operationConfig.joinToDataset.id,
    joinToPrefix: operationConfig.joinToPrefix,
    joinType: operationConfig.joinType,
  };
}

function parseFormValuesToStepOperationConfig(
  formValues: FormValues,
  inputDataset: DatasetResponse,
  joinToDataset: DatasetResponse,
): SaveWorkflowJoinStepRequest['operationConfig'] {
  const { conditions, dateInterval, ...rest } = formValues;
  const parsedConditions = [...conditions];
  if (dateInterval) {
    const leftDateIntervalProperty = getTimeSeriesDateRangeByTransformation(
      inputDataset,
      dateInterval,
    );
    const rightDateIntervalProperty = getTimeSeriesDateRangeByTransformation(
      joinToDataset,
      dateInterval,
    );
    if (!leftDateIntervalProperty || !rightDateIntervalProperty) {
      throw new Error(
        `Couldn't find data sources' fields that satisfy selected date interval`,
      );
    }
    parsedConditions.push({
      leftPropertyId: leftDateIntervalProperty.id,
      rightPropertyId: rightDateIntervalProperty.id,
    });
  }
  return {
    conditions: parsedConditions,
    ...rest,
  };
}

export function EditJoinStepModal({
  isNewStep,
  onClose,
  onSubmitStep,
  stepId,
  workflow,
}: CommonEditStepModalProps<SaveWorkflowJoinStepRequest>) {
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);

  const wizardPages = useMemo(
    () => [
      <JoinToModalPage
        isNewStep={isNewStep}
        setIsDataLoading={setIsNextButtonDisabled}
        stepId={stepId}
        workflow={workflow}
      />,
      <JoinConditionsModalPage stepId={stepId} workflow={workflow} />,
      <JoinTypeModalPage stepId={stepId} workflow={workflow} />,
    ],
    [isNewStep, stepId, workflow],
  );

  const submitStep = useCallback(
    async (data: FormValues) => {
      const stepInputDataset = findStepInputDataset(workflow, stepId);
      const { joinToDatasetId } = data;
      const joinToDataset = getDatasetCache({
        organizationId: workflow.organizationId,
        datasetId: joinToDatasetId,
      });
      if (!joinToDataset) {
        throw new Error(
          `EditJoinStepModal Error: Join-to dataset is not in local cache to save form values.`,
        );
      }
      const operationConfig = parseFormValuesToStepOperationConfig(
        data,
        stepInputDataset,
        joinToDataset,
      );
      const step: SaveWorkflowJoinStepRequest = {
        operationConfig,
        type: WorkflowStepOperationType.JOIN,
      };
      await onSubmitStep(step);
    },
    [onSubmitStep, stepId, workflow],
  );

  const initialValues = useMemo<DeepPartial<FormValues>>(() => {
    if (isNewStep) {
      return {};
    }
    const step = workflow.steps.find((s) => s.id === stepId);
    if (!step || step.type !== WorkflowStepOperationType.JOIN) {
      throw new Error(
        `EditJoinStepModal was opened to edit not join step. Type: ${step?.type}, step id: ${stepId}.`,
      );
    }
    const existingOperationConfig = step.operationConfig;
    const stepInputDataset = findStepInputDataset(workflow, stepId);
    return parseStepOperationConfigToFormValues(
      existingOperationConfig,
      stepInputDataset,
    );
  }, [isNewStep, stepId, workflow]);

  return (
    <ModalWizard<FormValues, DeepPartial<FormValues>>
      addArrayMutators
      initialValues={initialValues}
      isEditing={!isNewStep}
      isNextButtonDisabled={isNextButtonDisabled}
      itemLabel="connect step"
      onClose={onClose}
      onPageSubmit={submitStep}
      parentHandlesSuccessAndError
      showBackButton
      title="Connect"
      titleIcon={<JoinIcon />}
      wizardPages={wizardPages}
    />
  );
}
